import { makeObservable, observable, runInAction } from "mobx";
import { MrbBaseListViewStore } from "mrb/core";
import { MrbTableViewStore } from "mrb/components";
import { applicationDefaults } from "common/constants";
import { mrbDefaultFormats } from "mrb/common/formatting";
import { localizationService } from "common/localization";
import { AppVersionNameCell, AppVersionStatusCell } from "administration/app-release/components";
import { isStableVersionName } from "administration/app-release/utils";

class AppReleaseViewStore extends MrbBaseListViewStore {
    @observable.ref latestVersion = null;

    constructor(rootStore, { routeStore }) {
        super(rootStore, {
            queryConfig: {
                orderBy: "versionCode",
                orderDirection: "desc",
                pageSize: 10,
                disableUpdateQueryParams: true,
            },
        });
        makeObservable(this);
        this.routeStore = routeStore;

        this.setGridStore(
            new MrbTableViewStore(this.rootStore, this.queryUtility, {
                mapper: (item) => ({
                    ...item,
                    isLatest: !!this.latestVersion && this.latestVersion.id === item.id,
                    isBeta: !isStableVersionName(item.versionName),
                }),
                actions: {
                    enable: {
                        onClick: (item) => this.confirmToggle(item, true),
                        label: "ADMINISTRATION.APP_RELEASE.ACTIONS.ENABLE",
                    },
                    disable: {
                        onClick: (item) => this.confirmToggle(item, false),
                        label: "ADMINISTRATION.APP_RELEASE.ACTIONS.DISABLE",
                    },
                },
                columns: [
                    {
                        key: "versionName",
                        title: "ADMINISTRATION.APP_RELEASE.COLUMNS.VERSION_NAME",
                        cell: AppVersionNameCell,
                    },
                    {
                        key: "versionCode",
                        title: "ADMINISTRATION.APP_RELEASE.COLUMNS.VERSION_CODE",
                        sortable: true,
                    },
                    {
                        key: "isEnabled",
                        title: "ADMINISTRATION.APP_RELEASE.COLUMNS.STATUS",
                        cell: AppVersionStatusCell,
                    },
                    {
                        key: "isMandatory",
                        title: "ADMINISTRATION.APP_RELEASE.COLUMNS.MANDATORY",
                        format: (value) =>
                            localizationService.t(
                                value
                                    ? "ADMINISTRATION.APP_RELEASE.MANDATORY.YES"
                                    : "ADMINISTRATION.APP_RELEASE.MANDATORY.NO"
                            ),
                    },
                    {
                        key: "description",
                        title: "ADMINISTRATION.APP_RELEASE.COLUMNS.DESCRIPTION",
                    },
                    {
                        key: "dateCreated",
                        title: "ADMINISTRATION.APP_RELEASE.COLUMNS.DATE_CREATED",
                        sortable: true,
                        format: {
                            type: mrbDefaultFormats.date,
                            format: applicationDefaults.formats.dateTime,
                        },
                    },
                ],
            })
        );
    }

    async onInit() {
        await this.fetchLatestVersion();
        return super.onInit();
    }

    find(params) {
        return this.routeStore.findVersions(params);
    }

    onFetchError() {
        this.rootStore.notificationStore.error(localizationService.t("ADMINISTRATION.APP_RELEASE.LOAD_ERROR"));
    }

    // Any failure (404, or a 500 from the Embedded endpoint) means no enabled stable version, so no toast.
    fetchLatestVersion = async () => {
        let latest = null;
        try {
            latest = await this.routeStore.getLatestVersion();
        } catch {
            latest = null;
        }
        runInAction(() => {
            this.latestVersion = latest;
        });
    };

    confirmToggle = (item, enable) => {
        const key = enable ? "ENABLE" : "DISABLE";
        const params = { versionName: item.versionName, versionCode: item.versionCode };
        const isLatest = !enable && !!this.latestVersion && this.latestVersion.id === item.id;
        const descriptionKey = isLatest ? "DESCRIPTION_LATEST" : "DESCRIPTION";

        this.rootStore.confirmDialogStore.showConfirm({
            message: localizationService.t(`ADMINISTRATION.APP_RELEASE.CONFIRM.${key}.MESSAGE`, params),
            description: localizationService.t(`ADMINISTRATION.APP_RELEASE.CONFIRM.${key}.${descriptionKey}`, params),
            yesLabel: localizationService.t(`ADMINISTRATION.APP_RELEASE.CONFIRM.${key}.YES`),
            noLabel: localizationService.t("ADMINISTRATION.APP_RELEASE.CONFIRM.NO"),
            onConfirm: async () => {
                try {
                    if (enable) {
                        await this.routeStore.enableVersion(item.id);
                    } else {
                        await this.routeStore.disableVersion(item.id);
                    }
                    this.rootStore.notificationStore.success(
                        localizationService.t(`ADMINISTRATION.APP_RELEASE.${key}.SUCCESS`, params)
                    );
                    await this.fetchLatestVersion();
                    this.queryUtility.fetch();
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        localizationService.t(`ADMINISTRATION.APP_RELEASE.${key}.ERROR`, params),
                        err
                    );
                }
            },
        });
    };
}

export default AppReleaseViewStore;
